import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { useEffect, useMemo, useState } from 'react'
import { svgSearch } from 'stories/assets'
import { Input, Modal } from 'stories/components'
import { Tooltip } from 'stories/components/Tooltip/Tooltip'

import { partyHeaders } from './constant'
import type { IReportHeaders } from './types'

export const ReportPlaceholders = ({
  placeholders,
  selectedPlaces,
  setSelectedPlaces,
  type,
  setType,
  partyTemp,
}: {
  placeholders: Record<string, IReportHeaders[]>
  selectedPlaces: string[]
  setSelectedPlaces: Function
  type: string
  setType: Function
  partyTemp: Record<string, any>
}) => {
  const [query, setQuery] = useState('')

  const [collapseStatus, setCollapseStatus] = useState<Record<string, boolean>>({})
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [partyPlaceholders, setPartyPlaceholders] = useState<Array<string>>([])
  const [selectedParty, setSelectedParty] = useState('')

  useEffect(() => {
    setType('Loan')
  }, [])

  const hasTitlePrefix = (title: string) => {
    if (title.startsWith('Property')) return true
    if (title == 'Loan Status Date') return true

    return false
  }

  useEffect(() => {
    const temp: Record<string, boolean> = {}
    placeholders.Loan.map((item) => {
      if (hasTitlePrefix(item.title)) temp[item.title] = true
    })
    setCollapseStatus(temp)
  }, [placeholders])

  const allPlaceholders = useMemo(() => {
    if (!placeholders[type]) return []
    const list: string[] = []
    placeholders[type].map((item) => {
      if (hasTitlePrefix(item.title)) list.push(...item.data.map((v) => `${item.title} - ${v}`))
      list.push(...item.data)
    })
    return list
  }, [type, placeholders])

  const isSelectedAll = useMemo(() => {
    return allPlaceholders.every((placeholder) => selectedPlaces.includes(placeholder))
  }, [selectedPlaces, allPlaceholders])

  const isPartyPlaceholderSeletedAll = useMemo(
    () => Object.keys(partyHeaders).length === partyPlaceholders.length,
    [partyPlaceholders],
  )

  const filteredPlaceholders = useMemo(() => {
    if (!query) return placeholders[type] || []
    return placeholders[type].map((header) => {
      return {
        title: header.title,
        data: header.data.filter((v) => v.toLowerCase().includes(query.toLowerCase())).sort(),
      }
    })
  }, [type, placeholders, query])

  const onSelectPlaceholder = (value: string, title: string, shouldRemove: boolean = false) => {
    if (title === 'Parties') {
      setSelectedParty(value)
      const placeholders = selectedPlaces.filter((key) => key.includes(`${value}-`)).map((key) => key.split('-')[1])
      setPartyPlaceholders(placeholders)
      setIsModalOpen(true)
      return
    }
    if (hasTitlePrefix(title)) {
      value = `${title} - ${value}`
    }

    const newValues = cloneDeep(selectedPlaces)

    if (shouldRemove) {
      const index = newValues.indexOf(value)
      if (index !== -1) {
        newValues.splice(index, 1)
      }
    } else {
      newValues.push(value)
    }

    setSelectedPlaces(newValues)
  }

  const onToggle = (title: string) => {
    const newData = cloneDeep(collapseStatus)
    newData[title] = !newData[title]
    setCollapseStatus(newData)
  }

  const onSelectPartyPlaceholder = (value: string) => {
    const newValues = cloneDeep(partyPlaceholders)
    const index = newValues.indexOf(value)
    if (index == -1) newValues.push(value)
    else newValues.splice(index, 1)
    setPartyPlaceholders(newValues)
  }

  const onPartyPlaceholderSubmit = () => {
    if (!selectedParty) return
    let temp = cloneDeep(selectedPlaces)
    const newPlaceholders = partyPlaceholders.map((key) => `${selectedParty}-${key}`)
    const addingPlaceholders = newPlaceholders.filter((key) => !selectedPlaces.includes(key))
    temp = temp.filter((key) => !key.includes(`${selectedParty}-`) || newPlaceholders.includes(key))
    temp.push(...addingPlaceholders)
    setSelectedPlaces(temp)
    setPartyPlaceholders([])
    setSelectedParty('')
    setIsModalOpen(false)
  }

  return (
    <div className="border-r border-gray-200 pr-4">
      <div className="flex gap-4 items-center justify-between">
        <p className="text-lg font-bold mb-4">Placeholders</p>
        {/* <ButtonGroup
          title={['Loan', 'Trust Ledger']}
          value={type}
          onChange={(v) => {
            setType(v)
            setSelectedPlaces(['Loan #'])
          }}
        /> */}
      </div>

      <div className="flex items-center justify-between mb-2 gap-x-4">
        <div className="-mb-4 flex-1">
          <Input type="search" title="Search" hasIcon icon={svgSearch} value={query} onChange={setQuery} />
        </div>

        <p
          className="text-sm cursor-pointer text-shade-blue hover:underline"
          onClick={() => {
            if (isSelectedAll) {
              setSelectedPlaces([])
            } else {
              const newData = cloneDeep(selectedPlaces)
              allPlaceholders.forEach((placeholder) => {
                if (!newData.includes(placeholder)) {
                  newData.push(placeholder)
                }
              })
              setSelectedPlaces(newData)
            }
          }}
        >
          {isSelectedAll ? 'Unselect All' : 'Select All'}
        </p>
      </div>
      <div className="grid grid-cols-2 text-sm">
        {filteredPlaceholders.map((headers, index) => {
          const { title, data } = headers
          const isOpen = !collapseStatus[title]

          const checkSelected = (v: string) => {
            if (hasTitlePrefix(title)) v = `${title} - ${v}`
            return selectedPlaces.includes(v)
          }

          let isSelectedAll = data.length > 0 && data.every((v) => checkSelected(v))

          return (
            <>
              <p className="flex items-center gap-2 col-span-full px-2 py-1 bg-gray-100 font-semibold cursor-pointer">
                <span className="flex-1" onClick={() => onToggle(title)}>
                  {index + 1}. {title}
                </span>

                <span
                  className="text-sm cursor-pointer text-shade-blue hover:underline font-normal"
                  onClick={() => {
                    const newData = cloneDeep(selectedPlaces)
                    if (isSelectedAll) {
                      data.forEach((v) => {
                        if (hasTitlePrefix(title)) v = `${title} - ${v}`
                        for (let i = newData.length - 1; i >= 0; i--) {
                          if (newData[i] === v) {
                            newData.splice(i, 1)
                          }
                        }
                      })
                    } else {
                      data.forEach((v) => {
                        if (hasTitlePrefix(title)) v = `${title} - ${v}`
                        const index = newData.indexOf(v)
                        if (index == -1) newData.push(v)
                      })
                    }
                    setSelectedPlaces(newData)
                  }}
                >
                  {isSelectedAll ? 'Unselect All' : 'Select All'}
                </span>
                {isOpen ? <ChevronUpIcon className="w-4 h-4" /> : <ChevronDownIcon className="w-4 h-4" />}
              </p>
              {isOpen &&
                data
                  .map((key) => (headers.title === 'Parties' ? key.split('-')[0] : key))
                  .filter((value, index, self) => self.indexOf(value) === index)
                  .map((v, i) => {
                    let isSelected = checkSelected(v)
                    const isPartySection = headers.title === 'Parties'
                    let tooltip = ''
                    let selectionCount = 0

                    if (isPartySection) {
                      isSelected = !!selectedPlaces.filter((key) => key.includes(`${v}-`)).length
                      const selectedPartyPlaceholders = selectedPlaces
                        .filter((key) => key.includes(`${v}-`))
                        .map((key) => partyHeaders[key.split('-')[1]])
                        .sort()

                      selectedPartyPlaceholders.map((item, index: number) => {
                        if (index) tooltip += index % 5 == 0 ? ',\n' : ', '
                        tooltip += item
                      })
                    } else {
                      let checkValue = v
                      if (hasTitlePrefix(title)) checkValue = `${title} - ${v}`
                      selectionCount = selectedPlaces.filter((p) => p === checkValue).length
                      isSelected = selectionCount > 0
                    }

                    return (
                      <div
                        className={`flex gap-2 cursor-pointer items-center p-2 border-b border-gray-100 hover:bg-gray-100 ${
                          isSelected ? 'bg-gray-200' : ''
                        }`}
                      >
                        <div
                          className="flex items-center gap-1 flex-1"
                          onClick={() => onSelectPlaceholder(v, headers.title, false)}
                        >
                          <CheckIcon className={`${isSelected ? 'text-shade-blue' : 'text-gray-300'} w-4 h-4`} />
                          <Tooltip message={tooltip}>
                            <p className="text-sm">
                              {i + 1}. {partyTemp[v] || v}
                              {!isPartySection && selectionCount > 0 && (
                                <span className="ml-1 text-xs text-shade-blue font-semibold">({selectionCount})</span>
                              )}
                            </p>
                          </Tooltip>
                        </div>
                        {!isPartySection && selectionCount > 0 && (
                          <button
                            className="text-xs text-red-600 hover:text-red-800 px-1"
                            onClick={(e) => {
                              e.stopPropagation()
                              onSelectPlaceholder(v, headers.title, true)
                            }}
                            title="Remove one instance"
                          >
                            −
                          </button>
                        )}
                      </div>
                    )
                  })}
            </>
          )
        })}
      </div>
      {isModalOpen && (
        <Modal title="Party Details" isOpen onClose={() => setIsModalOpen(false)} onOk={onPartyPlaceholderSubmit}>
          <>
            <div className="flex justify-end">
              <span
                className="text-sm cursor-pointer text-shade-blue hover:underline font-normal"
                onClick={() => {
                  const newData = cloneDeep(partyPlaceholders)
                  if (isPartyPlaceholderSeletedAll) {
                    Object.keys(partyHeaders).forEach((v) => {
                      const index = newData.indexOf(v)
                      if (index != -1) newData.splice(index, 1)
                    })
                  } else {
                    Object.keys(partyHeaders).forEach((v) => {
                      const index = newData.indexOf(v)
                      if (index == -1) newData.push(v)
                    })
                  }
                  setPartyPlaceholders(newData)
                }}
              >
                {isPartyPlaceholderSeletedAll ? 'Unselect All' : 'Select All'}
              </span>
            </div>
            <div className="grid grid-cols-4">
              {Object.keys(partyHeaders).map((key: string, index: number) => {
                const isSelected = partyPlaceholders.includes(key)
                return (
                  <div
                    className={`flex gap-2 cursor-pointer items-center p-2 border-b border-gray-100 hover:bg-gray-100 ${
                      isSelected ? 'bg-gray-200' : ''
                    }`}
                    onClick={() => onSelectPartyPlaceholder(key)}
                  >
                    <CheckIcon className={`${isSelected ? 'text-shade-blue' : 'text-gray-300'} w-4 h-4`} />
                    <p className="text-sm">
                      {index + 1}. {partyHeaders[key]}
                    </p>
                  </div>
                )
              })}
            </div>
          </>
        </Modal>
      )}
    </div>
  )
}
